import React, { useState, useEffect } from 'react';
import { Form, Select, Switch, Card, Typography, Row, Col, Alert, Divider } from 'antd';
import { CustomerCategoryDTO, CreateCustomerCategoryDTO, UpdateCustomerCategoryDTO } from '../types';

const { Title, Text } = Typography;
const { Option } = Select;

// Define SubPartType enum values (matching backend)
export enum SubPartType {
    GROUP_MEMBER = 'GROUP_MEMBER',
    SIGNATORY = 'SIGNATORY',
    SHAREHOLDER = 'SHAREHOLDER',
    DIRECTOR = 'DIRECTOR',
    GUARANTOR = 'GUARANTOR',
    TRUSTEE = 'TRUSTEE'
}

// Define CustomerType enum values (matching backend)
export enum CustomerType {
    INDIVIDUAL = 'INDIVIDUAL',
    GROUP = 'GROUP',
    BUSINESS = 'BUSINESS'
}


export interface HierarchicalCustomerCategoryFormProps {
    formData: CreateCustomerCategoryDTO | UpdateCustomerCategoryDTO;
    onFormDataChange: (data: Partial<CreateCustomerCategoryDTO | UpdateCustomerCategoryDTO>) => void;
    memberTemplateCategories?: CustomerCategoryDTO[];
    disabled?: boolean;
    mode?: 'create' | 'edit' | 'view';
}

const HierarchicalCustomerCategoryForm: React.FC<HierarchicalCustomerCategoryFormProps> = ({
    formData,
    onFormDataChange,
    memberTemplateCategories = [],
    disabled = false,
    mode = 'create'
}) => {
    const [isSubPart, setIsSubPart] = useState(formData.isSubPart || false);
    const [selectedParentCategory, setSelectedParentCategory] = useState<CustomerCategoryDTO | null>(null);
    const [availableSubPartTypes, setAvailableSubPartTypes] = useState<SubPartType[]>([]);

    // Update selected parent category when parentCategoryId changes
    useEffect(() => {
        if (formData.parentCategoryId && Array.isArray(memberTemplateCategories) && memberTemplateCategories.length > 0) {
            const parentCategory = memberTemplateCategories.find(cat => cat.id === formData.parentCategoryId);
            setSelectedParentCategory(parentCategory || null);
        } else {
            setSelectedParentCategory(null);
        }
    }, [formData.parentCategoryId, memberTemplateCategories]);

    // Update available sub-part types based on parent category type
    useEffect(() => {
        if (selectedParentCategory) {
            const parentType = selectedParentCategory.customerType;
            let availableTypes: SubPartType[] = [];
            
            switch (parentType) {
                case CustomerType.GROUP:
                    availableTypes = [SubPartType.GROUP_MEMBER, SubPartType.GUARANTOR, SubPartType.TRUSTEE];
                    break;
                case CustomerType.BUSINESS:
                    availableTypes = [
                        SubPartType.SIGNATORY,
                        SubPartType.SHAREHOLDER,
                        SubPartType.DIRECTOR,
                        SubPartType.GUARANTOR,
                        SubPartType.TRUSTEE
                    ];
                    break;
                default:
                    availableTypes = [];
            }
            
            setAvailableSubPartTypes(availableTypes);
        } else {
            setAvailableSubPartTypes([]);
        }
    }, [selectedParentCategory]);

    const handleIsSubPartChange = (checked: boolean) => {
        setIsSubPart(checked);
        
        if (checked) {
            // When becoming a sub-part, force customer type to INDIVIDUAL
            onFormDataChange({
                isSubPart: true,
                customerType: CustomerType.INDIVIDUAL,
                // Clear configurations as sub-parts can't have them
                groupConfig: undefined,
                businessConfig: undefined
            });
        } else {
            // When becoming a main entity, clear sub-part related fields
            onFormDataChange({
                isSubPart: false,
                parentCategoryId: undefined,
                subPartType: undefined
            });
        }
    };

    const handleParentCategoryChange = (parentCategoryId: number) => {
        onFormDataChange({
            parentCategoryId,
            subPartType: undefined // Reset sub-part type when parent changes
        });
    };

    const handleSubPartTypeChange = (subPartType: SubPartType) => {
        onFormDataChange({ subPartType });
    };

    const handleCustomerTypeChange = (customerType: CustomerType) => {
        onFormDataChange({ 
            customerType,
            // Clear configurations when customer type changes
            groupConfig: undefined,
            businessConfig: undefined
        });
    };

    const getSubPartTypeDisplayName = (subPartType: SubPartType): string => {
        const displayNames = {
            [SubPartType.GROUP_MEMBER]: 'Group Member',
            [SubPartType.SIGNATORY]: 'Signatory',
            [SubPartType.SHAREHOLDER]: 'Shareholder',
            [SubPartType.DIRECTOR]: 'Director',
            [SubPartType.GUARANTOR]: 'Guarantor',
            [SubPartType.TRUSTEE]: 'Trustee'
        };
        return displayNames[subPartType] || subPartType;
    };

    const getSubPartTypeDescription = (subPartType: SubPartType): string => {
        const descriptions = {
            [SubPartType.GROUP_MEMBER]: 'Individual member of a group',
            [SubPartType.SIGNATORY]: 'Individual authorized to sign on behalf of business',
            [SubPartType.SHAREHOLDER]: 'Individual who owns shares in the business',
            [SubPartType.DIRECTOR]: 'Individual in leadership position within business',
            [SubPartType.GUARANTOR]: 'Individual who provides guarantee for obligations',
            [SubPartType.TRUSTEE]: 'Individual who manages assets on behalf of others'
        };
        return descriptions[subPartType] || '';
    };

    return (
        <Card>
            <Title level={4}>Customer Category Configuration</Title>
            
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form.Item
                        label="Category Type"
                        tooltip="Choose whether this is a member template or a main category"
                    >
                        <Switch
                            checked={isSubPart}
                            onChange={handleIsSubPartChange}
                            disabled={disabled || mode === 'view'}
                            checkedChildren="Member Template"
                            unCheckedChildren="Main Category"
                        />
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">
                                {isSubPart 
                                    ? "This template defines the structure for individuals within groups/businesses"
                                    : "This category is for standalone customers or main entities (groups/businesses)"
                                }
                            </Text>
                        </div>
                    </Form.Item>
                </Col>

                <Col span={24}>
                    <Form.Item
                        label="Customer Type"
                        tooltip={isSubPart ? "Member templates are always Individual type" : "Select the type of main entity this category represents"}
                    >
                        <Select
                            value={formData.customerType as unknown as CustomerType | null | undefined}
                            onChange={handleCustomerTypeChange}
                            disabled={disabled || mode === 'view' || isSubPart}
                            placeholder="Select customer type"
                        >
                            <Option value={CustomerType.INDIVIDUAL}>Individual</Option>
                            {!isSubPart && <Option value={CustomerType.GROUP}>Group</Option>}
                            {!isSubPart && <Option value={CustomerType.BUSINESS}>Business</Option>}
                        </Select>
                        {isSubPart && (
                            <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                                Member templates are always Individual type as they define individual member properties
                            </div>
                        )}
                    </Form.Item>
                </Col>

                {/* Groups/Businesses select member templates */}
                {(formData.customerType === CustomerType.GROUP || formData.customerType === CustomerType.BUSINESS) && !isSubPart && (
                    <>
                        <Col span={24}>
                            <Form.Item
                                label="Member Template"
                                tooltip="Select the template that defines the structure for individuals within this group/business"
                            >
                                <Select
                                    value={formData.parentCategoryId}
                                    onChange={handleParentCategoryChange}
                                    disabled={disabled || mode === 'view'}
                                    placeholder="Select member template (optional)"
                                    showSearch
                                    optionFilterProp="children"
                                    allowClear
                                >
                                    {Array.isArray(memberTemplateCategories) && memberTemplateCategories.map(category => (
                                        <Option key={category.id} value={category.id}>
                                            {category.name} ({category.subPartType})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {formData.parentCategoryId && (
                            <Col span={24}>
                                <Alert
                                    message="Member Template Selected"
                                    description={`This ${formData.customerType?.toLowerCase()} will use the selected template to define the structure for its individual members.`}
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            </Col>
                        )}
                    </>
                )}

                {/* Member templates define their role type */}
                {isSubPart && (
                    <Col span={24}>
                        <Form.Item
                            label="Member Role Type"
                            tooltip="Select the role this template represents for individuals within groups/businesses"
                            required
                        >
                            <Select
                                value={formData.subPartType as unknown as SubPartType | null | undefined}
                                onChange={handleSubPartTypeChange}
                                disabled={disabled || mode === 'view'}
                                placeholder="Select member role type"
                            >
                                <Option value={SubPartType.GROUP_MEMBER}>
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>Group Member</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>Regular member of a group</div>
                                    </div>
                                </Option>
                                <Option value={SubPartType.SIGNATORY}>
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>Signatory</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>Authorized to sign on behalf of business</div>
                                    </div>
                                </Option>
                                <Option value={SubPartType.SHAREHOLDER}>
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>Shareholder</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>Owns shares in the business</div>
                                    </div>
                                </Option>
                                <Option value={SubPartType.DIRECTOR}>
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>Director</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>Leadership position within business</div>
                                    </div>
                                </Option>
                                <Option value={SubPartType.GUARANTOR}>
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>Guarantor</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>Provides guarantee for obligations</div>
                                    </div>
                                </Option>
                                <Option value={SubPartType.TRUSTEE}>
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>Trustee</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>Manages assets on behalf of others</div>
                                    </div>
                                </Option>
                            </Select>
                        </Form.Item>
                    </Col>
                )}
            </Row>

            {!isSubPart && formData.customerType && (
                <>
                    <Divider />
                    <Alert
                        message={`${formData.customerType} Category Configuration`}
                        description={
                            formData.customerType === CustomerType.INDIVIDUAL
                                ? "Individual customers can be standalone or you can create member templates for them to be used by groups/businesses."
                                : `${formData.customerType} entities can select member templates to define the structure for their individual members.`
                        }
                        type="success"
                        showIcon
                    />
                </>
            )}

            {isSubPart && (
                <>
                    <Divider />
                    <Alert
                        message="Member Template Configuration"
                        description="This template will define the structure and fields for individuals within groups or businesses. Groups and businesses can select this template when they need members with this specific role."
                        type="info"
                        showIcon
                    />
                </>
            )}
        </Card>
    );
};

export default HierarchicalCustomerCategoryForm;
